import { useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiLock, FiPlay, FiUserPlus, FiCheckCircle, FiCalendar } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { createCycle, updateCycle, activateCycle, closeCycle, getCycleReadiness } from "../../services/cycleApi";
import { Card, PageHeader, Button, StatusBadge, ConfirmDialog, Loader, EmptyState, Banner } from "../../components/common";
import CycleFormModal from "./components/CycleFormModal";
import CycleEmployeePicker from "./components/CycleEmployeePicker";
import CycleDatesModal from "./components/CycleDatesModal"; // [NEW] PM-4
import { STAGE_LABELS, STAGE_ORDER } from "../../utils/constants";
import { formatDate } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";

const api = { createCycle, updateCycle, activateCycle, closeCycle, getCycleReadiness };

const CYCLE_TONE = { Active: "success", Draft: "neutral", Closed: "neutral", Cancelled: "neutral" };

function Readiness({ cycle }) {
  const [state, setState] = useState(null);
  const check = async () => {
    setState({ loading: true });
    try {
      const { data } = await api.getCycleReadiness(cycle.id);
      setState(data);
    } catch (err) {
      setState({ error: getErrorMessage(err) });
    }
  };
  return (
    <div className="stack">
      <Button variant="ghost" size="sm" icon={FiCheckCircle} onClick={check} loading={state?.loading}>Check launch readiness</Button>
      {state?.error && <Banner tone="danger" title="Couldn't check readiness">{state.error}</Banner>}
      {state && !state.loading && !state.error && (
        state.ready
          ? <Banner tone="success" title={`Ready to launch (${state.eligible_count} eligible employees)`}>Launching opens the cycle and notifies everyone.</Banner>
          : (
            <Banner tone="warning" title="Not ready yet">
              <ul className="icon-list">{state.issues.map((i) => <li key={i}>{i}</li>)}</ul>
            </Banner>
          )
      )}
    </div>
  );
}

export default function PerformanceCyclesPage() {
  const { cycles, loading, reload } = useCycle();
  const [editing, setEditing] = useState(undefined);
  const [confirm, setConfirm] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [changingDates, setChangingDates] = useState(null); // [NEW] PM-4: { cycle, stageKey }
  const [busyKey, setBusyKey] = useState("");

  const save = async (form) => {
    try {
      if (editing) await api.updateCycle(editing.id, form);
      else await api.createCycle(form);
      toast.success(editing ? "Cycle updated." : "Cycle created as a draft. Add employees, then launch it.");
      reload();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  const runConfirm = async () => {
    setBusyKey("confirm");
    try {
      if (confirm.type === "close") await api.closeCycle(confirm.cycle.id);
      else await api.activateCycle(confirm.cycle.id);
      toast.success(confirm.type === "close" ? `${confirm.cycle.name} closed.` : `${confirm.cycle.name} is now active.`);
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyKey("");
    }
  };

  if (loading && !cycles.length) return <Loader />;

  return (
    <div className="page">
      <PageHeader
        title="Performance cycles"
        subtitle="Set the review period (October to March) and stage windows, add employees from any department, then launch."
        actions={
          <>
            <Button icon={FiPlus} onClick={() => setEditing(null)}>New cycle</Button>
          </>
        }
      />

      <Banner tone="info" title="How stages open">
        Each stage accepts submissions between its start and end dates. To give one person more time, use
        Deadline override on their page under Review &amp; finalize. Use <strong>Add / remove employees</strong> to pick people by
        cost center (e.g. all of TTBS, or only some people of TTBS BSS Transformation) and by department; switch the
        filter and tick more; people already in the cycle are ticked, so untick someone to take them out.
      </Banner>

      {!cycles.length && <Card><EmptyState title="No cycles yet" text="Create the first performance cycle to begin goal setting." /></Card>}

      {cycles.map((cycle) => {
        const stages = STAGE_ORDER.map((k) => cycle.stages.find((s) => s.key === k)).filter(Boolean);
        return (
          <Card
            key={cycle.id}
            title={cycle.name}
            subtitle={`${formatDate(cycle.startDate)} to ${formatDate(cycle.endDate)}`}
            actions={
              <>
                <StatusBadge status={cycle.status} tone={CYCLE_TONE[cycle.status]} />
                {cycle.status === "Draft" && <Button variant="ghost" size="sm" icon={FiEdit2} onClick={() => setEditing(cycle)}>Edit</Button>}
                {/* [NEW] PM-4: the dates of a running cycle can be changed too (for every employee in it) */}
                {cycle.status === "Active" && <Button variant="ghost" size="sm" icon={FiCalendar} onClick={() => setChangingDates({ cycle })}>Change dates</Button>}
                {["Draft", "Active"].includes(cycle.status) && <Button variant="secondary" size="sm" icon={FiUserPlus} onClick={() => setMapping(cycle)}>Add / remove employees</Button>}
                {cycle.status === "Draft" && <Button size="sm" icon={FiPlay} onClick={() => setConfirm({ type: "activate", cycle })}>Launch</Button>}
                {cycle.status === "Active" && <Button variant="danger" size="sm" icon={FiLock} onClick={() => setConfirm({ type: "close", cycle })}>Close cycle</Button>}
              </>
            }
          >
            <div className="stage-table">
              {stages.map((s, i) => (
                <div key={s.key} className={`stage-table-row is-${s.status}`}>
                  <span className="stage-edit-num">{i + 1}</span>
                  <span className="stage-table-name">{STAGE_LABELS[s.key]}</span>
                  <span className="stage-table-dates">{formatDate(s.startDate)} to {formatDate(s.endDate)}</span>
                  <StatusBadge status={s.status} />
                  {/* [NEW] PM-4: edit the dates of any stage (Open, Upcoming, Closed) while the cycle is not closed */}
                  {["Draft", "Active"].includes(cycle.status)
                    ? <Button variant="ghost" size="sm" icon={FiEdit2} onClick={() => setChangingDates({ cycle, stageKey: s.key })} aria-label={`Edit ${STAGE_LABELS[s.key]} dates`}>Edit dates</Button>
                    : <span />}
                </div>
              ))}
            </div>
            <p className="footnote">
              Release: {cycle.workflow.release_mode === "ON_FINALIZE" ? "automatic on finalization" : "manual by HR"}.
              Acknowledgment {cycle.workflow.acknowledgment_required ? "required" : "optional"}.
              Manager feedback {cycle.workflow.manager_feedback_visible ? "visible" : "hidden"} to employees.
            </p>
            {cycle.status === "Draft" && <Readiness cycle={cycle} />}
          </Card>
        );
      })}

      <CycleFormModal open={editing !== undefined} cycle={editing} onClose={() => setEditing(undefined)} onSave={save} />
      <CycleEmployeePicker cycle={mapping} onClose={() => setMapping(null)} onDone={() => { setMapping(null); reload(); }} />
      <CycleDatesModal cycle={changingDates?.cycle || null} stageKey={changingDates?.stageKey} onClose={() => setChangingDates(null)} onDone={reload} />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.type === "close" ? `Close ${confirm?.cycle.name}?` : `Launch ${confirm?.cycle.name}?`}
        message={
          confirm?.type === "close"
            ? "Closing needs every eligible appraisal to be released (and acknowledged, if required). Nobody can edit afterwards."
            : "The cycle opens and every mapped employee is notified. Scoring policy and rating scheme must be approved."
        }
        confirmLabel={confirm?.type === "close" ? "Close cycle" : "Launch cycle"}
        tone={confirm?.type === "close" ? "danger" : "primary"}
        loading={busyKey === "confirm"}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      >
        {confirm?.type === "activate" && (() => {
          const past = (confirm.cycle.stages || []).filter((st) => st.endAt && new Date(st.endAt) <= new Date());
          if (!past.length) return null;
          return (
            <Banner tone="danger" title="Some windows have already ended">
              {past.map((st) => `${STAGE_LABELS[st.key]} ended on ${formatDate(st.endDate)}`).join("; ")}.
              {" "}People won&apos;t be able to do these steps after launch (only one person at a time can be given a
              deadline override). Edit the cycle dates first if this cycle has no employees yet.
            </Banner>
          );
        })()}
      </ConfirmDialog>
    </div>
  );
}